/**
 * P - Instruction Dialog — mirror Access popup `frmMatchingPSMainSubFormStockP`
 * (file frontend/plan/access_all/form_frmMatchingPSMainSubFormStockP.txt
 *  + spec frontend/plan/frmMatchingPSMainSubFormStockP_AI_Web_Conversion_Spec.md).
 *
 * Layout 4 vùng (spec §2.3):
 *   ┌─ Header: Match Date | Note ──────────────────────────────────────────┐
 *   ├─ Purchase filter+grid (left) ──── Factory filter+stock grid (right) ─┤
 *   ├─ Toolbar: helper + [Save Plan] ──────────────────────────────────────┤
 *   └─ Detail grid (P-Stock instructions) + [Delete Plan] toolbar ─────────┘
 *
 * Logic mirror VBA `btnSavePlan_Click` (spec §8):
 *   - factory_name required ("Please choose TLC Factory!").
 *   - ≥1 row ticked ("Please tick at least one record!").
 *   - matchKg < 0 reject; matchKg > pending reject; blank → fallback pending.
 *   - Bulk insert vào public.match_p_stock (KHÔNG soft-delete như Edit Plan).
 * Backend re-validate qua POST /matching-ps/stock-p/plans.
 */
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import RefreshIcon from '@mui/icons-material/Refresh'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'

import { useSnackbar } from '../../../hooks/useSnackbar'
import {
  useDeleteStockPPlanMutation,
  useGetMatchCategoriesQuery,
  useGetStockPFactoryOptionsQuery,
  useGetStockPFactoryStockQuery,
  useGetStockPPlansQuery,
  useGetStockPPurchaseCandidatesQuery,
  useSaveStockPPlanMutation,
} from '../../../store/api/matchingPsApi'

/* ---------- formatters (đồng nhất với DirectPSDialog) -------------------- */

const ACCESS_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const

function fmtDate(value: unknown): string {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  return `${day}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()}`
}

function fmtNumber(value: unknown): string {
  if (value == null || value === '') return ''
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return String(value)
  const txt = Math.abs(Math.round(n)).toLocaleString('en-US')
  return n < 0 ? `(${txt})` : txt
}

function toNumber(v: unknown): number {
  if (v == null || v === '') return 0
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : 0
}

/* ---------- defaults ----------------------------------------------------- */

type DateRange = { from: string; to: string }

const ACCENT = '#1e88e5' // P side accent

function defaultFiscalRange(now: Date = new Date()): DateRange {
  const y = now.getFullYear()
  const inQ4 = now.getMonth() >= 9
  const startYear = inQ4 ? y : y - 1
  const endYear = inQ4 ? y + 1 : y
  return { from: `${startYear}-10-01`, to: `${endYear}-09-30` }
}

/* ---------- column defs -------------------------------------------------- */

interface ColDef {
  key: string
  label: string
  width: number
  kind?: 'date' | 'number' | 'text'
  align?: 'left' | 'right'
}

const P_CANDIDATE_COLS: ColDef[] = [
  { key: 'contractNo', label: 'ContractNo', width: 100 },
  { key: 'latestDelivery', label: 'LatestDelivery', width: 100, kind: 'date' },
  { key: 'cpName', label: 'Supplier', width: 140 },
  { key: 'deliveryPlace', label: 'Place', width: 100 },
  { key: 'quantityKg', label: 'ContractQuant', width: 86, kind: 'number', align: 'right' },
  { key: 'pPlanPending', label: 'Pending', width: 80, kind: 'number', align: 'right' },
  { key: 'qualityCode', label: 'Quality', width: 60 },
  { key: 'category', label: 'Grade', width: 60 },
]

const STOCK_COLS: ColDef[] = [
  { key: 'qualityCode', label: 'Quality', width: 70 },
  { key: 'grade', label: 'Grade', width: 70 },
  { key: 'description', label: 'Description', width: 140 },
  { key: 'stockNw', label: 'Stock NW', width: 80, kind: 'number', align: 'right' },
  { key: 'stockAvailable', label: 'Available', width: 80, kind: 'number', align: 'right' },
  { key: 'pPlan', label: 'P Plan', width: 72, kind: 'number', align: 'right' },
  { key: 'pExecuted', label: 'P Executed', width: 84, kind: 'number', align: 'right' },
  { key: 'pToExecute', label: 'P ToExec', width: 76, kind: 'number', align: 'right' },
  { key: 'sPlan', label: 'S Plan', width: 72, kind: 'number', align: 'right' },
  { key: 'sExecuted', label: 'S Executed', width: 84, kind: 'number', align: 'right' },
  { key: 'sToExecute', label: 'S ToExec', width: 76, kind: 'number', align: 'right' },
]

const DETAIL_COLS: ColDef[] = [
  { key: 'matchId', label: 'ID', width: 60, kind: 'number', align: 'right' },
  { key: 'matchDate', label: 'InstructDate', width: 100, kind: 'date' },
  { key: 'matchKg', label: 'InstructKG', width: 88, kind: 'number', align: 'right' },
  { key: 'pSide', label: 'P Contract', width: 100 },
  { key: 'pName', label: 'P Supplier', width: 130 },
  { key: 'pQuality', label: 'P Quality', width: 70 },
  { key: 'pGrade', label: 'P Grade', width: 70 },
  { key: 'factoryName', label: 'StockSide', width: 100 },
  { key: 'pQuantity', label: 'P Quantity', width: 86, kind: 'number', align: 'right' },
  { key: 'pPlanPending', label: 'P Pending', width: 86, kind: 'number', align: 'right' },
  { key: 'pDirectS', label: 'P Direct', width: 80, kind: 'number', align: 'right' },
  { key: 'pInstructBaoloc', label: 'P InstructBL', width: 88, kind: 'number', align: 'right' },
  { key: 'pInstructNhontrach', label: 'P InstructNT', width: 88, kind: 'number', align: 'right' },
  { key: 'pPlace', label: 'P DeliveryPlace', width: 120 },
  { key: 'npeRef', label: 'P NpeRef', width: 90 },
  { key: 'note', label: 'Note', width: 140 },
  { key: 'dateUpdated', label: 'Updated Date', width: 130 },
  { key: 'personUpdated', label: 'Updated By', width: 130 },
]

function fmtCell(row: Record<string, unknown>, col: ColDef): string {
  const v = row[col.key]
  if (col.kind === 'date') return fmtDate(v)
  if (col.kind === 'number') return fmtNumber(v)
  if (v == null || v === '') return ''
  return String(v)
}

/* ---------- props -------------------------------------------------------- */

export interface StockPInstructionDialogProps {
  open: boolean
  onClose: () => void
  canEdit: boolean
}

export default function StockPInstructionDialog({
  open,
  onClose,
  canEdit,
}: StockPInstructionDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{ sx: { height: '95vh', maxHeight: '95vh' } }}
    >
      {open ? <StockPInstructionDialogBody onClose={onClose} canEdit={canEdit} /> : null}
    </Dialog>
  )
}

/* ---------- body --------------------------------------------------------- */

function StockPInstructionDialogBody({
  onClose,
  canEdit,
}: {
  onClose: () => void
  canEdit: boolean
}) {
  const snack = useSnackbar()
  const fiscalDefault = useMemo(() => defaultFiscalRange(), [])
  const today = useMemo(() => dayjs().format('YYYY-MM-DD'), [])

  // Header inputs
  const [matchDate, setMatchDate] = useState(today)
  const [note, setNote] = useState('')

  // Purchase filter
  const [categoryP, setCategoryP] = useState('')
  const [rangeP, setRangeP] = useState<DateRange>(fiscalDefault)

  // Factory filter
  const [categoryF, setCategoryF] = useState('')
  const [factoryCode, setFactoryCode] = useState('')

  // Selection / row inputs
  const [checked, setChecked] = useState<Set<string>>(new Set())
  const [matchKgMap, setMatchKgMap] = useState<Record<string, string>>({})
  const [selectedDetailId, setSelectedDetailId] = useState<number | null>(null)

  /* ---------- fetches --------------------------------------------------- */

  const categoryOptionsQuery = useGetMatchCategoriesQuery()
  const categoryOptions = categoryOptionsQuery.data ?? []

  const factoryOptionsQuery = useGetStockPFactoryOptionsQuery()
  const factoryOptions = factoryOptionsQuery.data ?? []

  // Auto-pick first factory option when loaded (Access form Form_Load behavior:
  // đọc filter từ tmp_filter_date, default 'baoloc'). Webapp default = first
  // factory option để user thấy stock grid ngay khi mở dialog.
  useEffect(() => {
    if (!factoryCode && factoryOptions.length > 0) {
      setFactoryCode(factoryOptions[0].factory_code)
    }
  }, [factoryCode, factoryOptions])

  const candidatesQuery = useGetStockPPurchaseCandidatesQuery({
    startDate: rangeP.from || undefined,
    endDate: rangeP.to || undefined,
    category: categoryP || undefined,
  })
  const stockQuery = useGetStockPFactoryStockQuery({
    factoryCode: factoryCode || undefined,
    category: categoryF || undefined,
  })
  const plansQuery = useGetStockPPlansQuery({
    factoryCode: factoryCode || undefined,
    category: categoryF || undefined,
  })

  const candidates = useMemo(
    () => (candidatesQuery.data ?? []) as Record<string, unknown>[],
    [candidatesQuery.data],
  )
  const stockRows = useMemo(
    () => (stockQuery.data ?? []) as Record<string, unknown>[],
    [stockQuery.data],
  )
  const detailRows = useMemo(
    () => (plansQuery.data ?? []) as Record<string, unknown>[],
    [plansQuery.data],
  )

  /* ---------- mutations -------------------------------------------------- */

  const [savePlan, { isLoading: saving }] = useSaveStockPPlanMutation()
  const [deletePlan, { isLoading: deleting }] = useDeleteStockPPlanMutation()

  /* ---------- handlers --------------------------------------------------- */

  const toggleRow = (contractNo: string) => {
    setChecked((prev) => {
      const next = new Set(prev)
      if (next.has(contractNo)) next.delete(contractNo)
      else next.add(contractNo)
      return next
    })
  }

  const allChecked =
    candidates.length > 0 &&
    candidates.every((r) => checked.has(String(r.contractNo ?? '')))
  const someChecked = candidates.some((r) => checked.has(String(r.contractNo ?? '')))

  const toggleAll = () => {
    if (allChecked) setChecked(new Set())
    else setChecked(new Set(candidates.map((r) => String(r.contractNo ?? ''))))
  }

  const setMatchKg = (contractNo: string, value: string) => {
    setMatchKgMap((prev) => ({ ...prev, [contractNo]: value }))
  }

  const handleSave = async () => {
    if (!canEdit) {
      snack.error("You don't have permission for this action.")
      return
    }
    if (!factoryCode) {
      snack.warning('Please choose TLC Factory!')
      return
    }
    if (!matchDate) {
      snack.error('Match Date is required.')
      return
    }
    const items = candidates
      .filter((r) => checked.has(String(r.contractNo ?? '')))
      .map((r) => {
        const cn = String(r.contractNo ?? '')
        const raw = matchKgMap[cn]
        const mk = raw == null || raw === '' ? null : Number(raw)
        return {
          pSide: cn,
          planPending: toNumber(r.pPlanPending),
          matchKg: Number.isFinite(mk as number) ? (mk as number) : null,
        }
      })
    if (items.length === 0) {
      snack.warning('Please tick at least one record!')
      return
    }
    for (const it of items) {
      const qty = it.matchKg ?? it.planPending
      if (qty < 0) {
        snack.warning('Cannot input KG(EDIT) < 0 !')
        return
      }
      if (qty > it.planPending) {
        snack.warning('Cannot input KG(EDIT) > Pending !')
        return
      }
    }
    try {
      const res = await savePlan({
        matchDate,
        note,
        factoryName: factoryCode,
        items,
      }).unwrap()
      snack.success(res.message || 'Successfully Updated')
      setChecked(new Set())
      setMatchKgMap({})
    } catch (err: unknown) {
      const msg =
        (err as { data?: { detail?: string } })?.data?.detail ??
        (err as { message?: string })?.message ??
        'Save failed'
      snack.error(msg)
    }
  }

  const handleDelete = async () => {
    if (!canEdit) {
      snack.error("You don't have permission for this action.")
      return
    }
    if (selectedDetailId == null) {
      snack.warning('Select a P-Stock row below before clicking Delete Plan.')
      return
    }
    if (!window.confirm(`Do you want to delete plan with ID = '${selectedDetailId}'?`)) {
      return
    }
    try {
      const res = await deletePlan({ matchId: selectedDetailId }).unwrap()
      snack.success(res.message || 'Successfully Deleted')
      setSelectedDetailId(null)
    } catch (err: unknown) {
      const msg =
        (err as { data?: { detail?: string } })?.data?.detail ??
        (err as { message?: string })?.message ??
        'Delete failed'
      snack.error(msg)
    }
  }

  const handleReloadAll = () => {
    void candidatesQuery.refetch()
    void stockQuery.refetch()
    void plansQuery.refetch()
  }

  /* ---------- render ----------------------------------------------------- */

  return (
    <>
      <DialogTitle
        component="div"
        sx={{ pl: 2, pr: 1, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <Chip
          size="small"
          label="P"
          sx={{
            height: 20,
            bgcolor: ACCENT,
            color: '#fff',
            fontWeight: 700,
            '& .MuiChip-label': { px: 0.9 },
          }}
        />
        <Typography component="span" variant="subtitle1" sx={{ fontWeight: 800, fontSize: '0.95rem' }}>
          P - INSTRUCTION
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Reload all grids">
          <span>
            <IconButton size="small" onClick={handleReloadAll} disabled={saving || deleting}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {!canEdit ? (
          <Alert severity="info" sx={{ py: 0.25 }}>
            Read-only — you don't have edit permission for this module.
          </Alert>
        ) : null}

        {/* Header inputs */}
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
          sx={{ px: 0.5 }}
        >
          <DatePicker
            label="Match Date"
            format="DD-MMM-YYYY"
            value={matchDate ? dayjs(matchDate) : null}
            onChange={(d) => setMatchDate(d && d.isValid() ? d.format('YYYY-MM-DD') : '')}
            slotProps={{
              textField: {
                size: 'small',
                sx: { minWidth: 170, '& .MuiInputBase-input': { fontSize: '0.78rem', py: 0.6 } },
              },
            }}
          />
          <TextField
            label="Note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            size="small"
            sx={{
              flex: 1,
              minWidth: 240,
              '& .MuiInputBase-input': { fontSize: '0.78rem', py: 0.6 },
            }}
          />
        </Stack>

        {/* 2-column area: Purchase | Factory stock */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 1,
            flex: '0 1 auto',
          }}
        >
          {/* Purchase */}
          <Paper
            elevation={0}
            sx={{
              border: 1,
              borderLeftWidth: 3,
              borderLeftColor: ACCENT,
              borderColor: 'divider',
              borderRadius: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              useFlexGap
              flexWrap="wrap"
              sx={{
                px: 1,
                py: 0.5,
                bgcolor: alpha(ACCENT, 0.08),
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Chip
                size="small"
                label="P"
                sx={{
                  height: 18,
                  bgcolor: ACCENT,
                  color: '#fff',
                  fontWeight: 700,
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.76rem' }}>
                Purchase
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ ml: 0.75, color: 'text.secondary', fontSize: '0.68rem' }}
                >
                  ({candidates.length.toLocaleString('en-US')})
                </Typography>
                {candidatesQuery.isFetching ? (
                  <CircularProgress size={10} sx={{ ml: 0.5, color: ACCENT }} />
                ) : null}
              </Typography>
            </Stack>

            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              useFlexGap
              flexWrap="wrap"
              sx={{ px: 1, py: 0.6 }}
            >
              <TextField
                select
                size="small"
                label="Grade"
                value={categoryP}
                onChange={(e) => setCategoryP(e.target.value)}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
                sx={{
                  flex: '1 1 0',
                  minWidth: 110,
                  '& .MuiInputBase-input': { fontSize: '0.72rem', py: 0.5 },
                }}
              >
                <MenuItem value="">
                  <em>(all)</em>
                </MenuItem>
                {categoryOptions.map((opt) => (
                  <MenuItem key={opt} value={opt} sx={{ fontSize: '0.74rem' }}>
                    {opt}
                  </MenuItem>
                ))}
              </TextField>
              <DatePicker
                label="Latest Delivery"
                format="DD-MMM-YYYY"
                value={rangeP.from ? dayjs(rangeP.from) : null}
                onChange={(d) =>
                  setRangeP({ ...rangeP, from: d && d.isValid() ? d.format('YYYY-MM-DD') : '' })
                }
                slotProps={{
                  textField: {
                    size: 'small',
                    InputLabelProps: { shrink: true },
                    sx: {
                      flex: '1 1 0',
                      minWidth: 140,
                      '& .MuiInputBase-input': { fontSize: '0.72rem', py: 0.5 },
                    },
                  },
                }}
              />
              <ArrowRightAltIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
              <DatePicker
                label="to"
                format="DD-MMM-YYYY"
                value={rangeP.to ? dayjs(rangeP.to) : null}
                onChange={(d) =>
                  setRangeP({ ...rangeP, to: d && d.isValid() ? d.format('YYYY-MM-DD') : '' })
                }
                slotProps={{
                  textField: {
                    size: 'small',
                    InputLabelProps: { shrink: true },
                    sx: {
                      flex: '1 1 0',
                      minWidth: 140,
                      '& .MuiInputBase-input': { fontSize: '0.72rem', py: 0.5 },
                    },
                  },
                }}
              />
            </Stack>

            <Box
              sx={{
                flex: 1,
                minHeight: 220,
                maxHeight: '38vh',
                overflow: 'auto',
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      padding="checkbox"
                      sx={{
                        width: 36,
                        minWidth: 36,
                        bgcolor: (t) => alpha(t.palette.text.primary, 0.05),
                        py: 0.3,
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={allChecked}
                        indeterminate={!allChecked && someChecked}
                        onChange={toggleAll}
                        sx={{ p: 0.25 }}
                      />
                    </TableCell>
                    {P_CANDIDATE_COLS.map((col) => (
                      <TableCell
                        key={col.key}
                        align={col.align ?? 'left'}
                        sx={{
                          width: col.width,
                          minWidth: col.width,
                          maxWidth: col.width,
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          py: 0.4,
                          px: 0.5,
                          bgcolor: (t) => alpha(t.palette.text.primary, 0.05),
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {col.label}
                      </TableCell>
                    ))}
                    <TableCell
                      sx={{
                        width: 96,
                        minWidth: 96,
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        py: 0.4,
                        px: 0.5,
                        bgcolor: (t) => alpha(t.palette.text.primary, 0.05),
                        whiteSpace: 'nowrap',
                      }}
                    >
                      KG(EDIT)
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {candidates.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={P_CANDIDATE_COLS.length + 2}
                        sx={{ textAlign: 'center', py: 3 }}
                      >
                        <Typography variant="caption" color="text.disabled">
                          No Purchase with pending &gt; 0.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    candidates.map((row, idx) => {
                      const cn = String(row.contractNo ?? '')
                      const isChecked = checked.has(cn)
                      return (
                        <TableRow
                          key={`${cn}-${idx}`}
                          hover
                          sx={{
                            ...(isChecked && {
                              '& .MuiTableCell-root': {
                                bgcolor: `${alpha(ACCENT, 0.14)} !important`,
                              },
                            }),
                          }}
                        >
                          <TableCell padding="checkbox" sx={{ py: 0.25 }}>
                            <Checkbox
                              size="small"
                              checked={isChecked}
                              onChange={() => toggleRow(cn)}
                              sx={{ p: 0.25 }}
                            />
                          </TableCell>
                          {P_CANDIDATE_COLS.map((col) => {
                            const text = fmtCell(row, col)
                            return (
                              <TableCell
                                key={col.key}
                                align={col.align ?? 'left'}
                                sx={{
                                  width: col.width,
                                  minWidth: col.width,
                                  maxWidth: col.width,
                                  fontSize: '0.68rem',
                                  py: 0.3,
                                  px: 0.5,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                                title={text}
                              >
                                {text}
                              </TableCell>
                            )
                          })}
                          <TableCell sx={{ py: 0.2, px: 0.4, width: 96, minWidth: 96 }}>
                            <TextField
                              size="small"
                              type="number"
                              placeholder={String(fmtNumber(row.pPlanPending) || '')}
                              value={matchKgMap[cn] ?? ''}
                              onChange={(e) => setMatchKg(cn, e.target.value)}
                              inputProps={{
                                min: 0,
                                style: {
                                  fontSize: '0.68rem',
                                  padding: '2px 4px',
                                  textAlign: 'right',
                                },
                              }}
                              sx={{
                                width: 86,
                                '& .MuiOutlinedInput-root': { fontSize: '0.68rem' },
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      )
                    })
                  )}
                </TableBody>
              </Table>
            </Box>
          </Paper>

          {/* Factory stock grid */}
          <Paper
            elevation={0}
            sx={{
              border: 1,
              borderLeftWidth: 3,
              borderLeftColor: '#43a047',
              borderColor: 'divider',
              borderRadius: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              useFlexGap
              flexWrap="wrap"
              sx={{
                px: 1,
                py: 0.5,
                bgcolor: alpha('#43a047', 0.08),
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Chip
                size="small"
                label="TLC"
                sx={{
                  height: 18,
                  bgcolor: '#43a047',
                  color: '#fff',
                  fontWeight: 700,
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.76rem' }}>
                Factory Stock / Quality
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ ml: 0.75, color: 'text.secondary', fontSize: '0.68rem' }}
                >
                  ({stockRows.length.toLocaleString('en-US')})
                </Typography>
                {stockQuery.isFetching ? (
                  <CircularProgress size={10} sx={{ ml: 0.5, color: '#43a047' }} />
                ) : null}
              </Typography>
            </Stack>

            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              useFlexGap
              flexWrap="wrap"
              sx={{ px: 1, py: 0.6 }}
            >
              <TextField
                select
                size="small"
                label="Grade"
                value={categoryF}
                onChange={(e) => setCategoryF(e.target.value)}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
                sx={{
                  flex: '1 1 0',
                  minWidth: 110,
                  '& .MuiInputBase-input': { fontSize: '0.72rem', py: 0.5 },
                }}
              >
                <MenuItem value="">
                  <em>(all)</em>
                </MenuItem>
                {categoryOptions.map((opt) => (
                  <MenuItem key={opt} value={opt} sx={{ fontSize: '0.74rem' }}>
                    {opt}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Factory"
                value={factoryCode}
                onChange={(e) => setFactoryCode(e.target.value)}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
                sx={{
                  flex: '1.5 1 0',
                  minWidth: 180,
                  '& .MuiInputBase-input': { fontSize: '0.72rem', py: 0.5 },
                }}
              >
                <MenuItem value="">
                  <em>(none)</em>
                </MenuItem>
                {factoryOptions.map((opt) => (
                  <MenuItem
                    key={opt.factory_code}
                    value={opt.factory_code}
                    sx={{ fontSize: '0.74rem' }}
                  >
                    {opt.factory_code}
                    {opt.receive_warehouse ? ` - ${opt.receive_warehouse}` : ''}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <Box
              sx={{
                flex: 1,
                minHeight: 220,
                maxHeight: '38vh',
                overflow: 'auto',
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    {STOCK_COLS.map((col) => (
                      <TableCell
                        key={col.key}
                        align={col.align ?? 'left'}
                        sx={{
                          width: col.width,
                          minWidth: col.width,
                          maxWidth: col.width,
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          py: 0.4,
                          px: 0.5,
                          bgcolor: (t) => alpha(t.palette.text.primary, 0.05),
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {col.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stockRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={STOCK_COLS.length} sx={{ textAlign: 'center', py: 3 }}>
                        <Typography variant="caption" color="text.disabled">
                          {factoryCode
                            ? 'No stock/quality rows for this factory.'
                            : 'Choose a factory to view stock.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    stockRows.map((row, idx) => (
                      <TableRow key={`stk-${idx}`} hover>
                        {STOCK_COLS.map((col) => {
                          const text = fmtCell(row, col)
                          return (
                            <TableCell
                              key={col.key}
                              align={col.align ?? 'left'}
                              sx={{
                                width: col.width,
                                minWidth: col.width,
                                maxWidth: col.width,
                                fontSize: '0.68rem',
                                py: 0.3,
                                px: 0.5,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                              title={text}
                            >
                              {text}
                            </TableCell>
                          )
                        })}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>
          </Paper>
        </Box>

        {/* Save toolbar + helper */}
        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
          <Alert
            severity="info"
            icon={false}
            sx={{
              flex: 1,
              minWidth: 0,
              py: 0.25,
              px: 1,
              fontSize: '0.72rem',
              '& .MuiAlert-message': { py: 0.25 },
            }}
          >
            Tick Purchase rows and choose a TLC Factory. Leave KG(EDIT) blank to apply the full
            Pending amount. Each ticked row creates one stock plan entry.
          </Alert>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={
              saving ? <CircularProgress size={12} sx={{ color: 'inherit' }} /> : <SaveOutlinedIcon />
            }
            onClick={handleSave}
            disabled={!canEdit || saving}
          >
            Save Plan
          </Button>
        </Stack>

        <Divider />

        {/* Detail grid header */}
        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
          <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: '0.78rem' }}>
            P-STOCK INSTRUCTIONS
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
            ({detailRows.length.toLocaleString('en-US')} rows)
          </Typography>
          {plansQuery.isFetching ? <CircularProgress size={11} sx={{ ml: 0.5 }} /> : null}
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>
            {selectedDetailId == null
              ? 'Click a row to select, then click Delete Plan.'
              : `Selected ID: ${selectedDetailId}`}
          </Typography>
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={
              deleting ? <CircularProgress size={12} sx={{ color: 'inherit' }} /> : <DeleteOutlineIcon />
            }
            onClick={handleDelete}
            disabled={!canEdit || deleting || selectedDetailId == null}
          >
            Delete Plan
          </Button>
        </Stack>

        <Box
          sx={{
            flex: 1,
            minHeight: 200,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'auto',
          }}
        >
          <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                {DETAIL_COLS.map((col) => (
                  <TableCell
                    key={col.key}
                    align={col.align ?? 'left'}
                    sx={{
                      width: col.width,
                      minWidth: col.width,
                      maxWidth: col.width,
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      py: 0.4,
                      px: 0.5,
                      bgcolor: (t) => alpha(t.palette.text.primary, 0.05),
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {detailRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={DETAIL_COLS.length} sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="caption" color="text.disabled">
                      No active P-Stock instructions for this filter.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                detailRows.map((row, idx) => {
                  const mid = toNumber(row.matchId)
                  const isSelected = selectedDetailId === mid
                  return (
                    <TableRow
                      key={`${mid}-${idx}`}
                      hover
                      onClick={() => setSelectedDetailId(mid || null)}
                      sx={{
                        cursor: 'pointer',
                        ...(isSelected && {
                          '& .MuiTableCell-root': {
                            bgcolor: (t) => `${alpha(t.palette.primary.main, 0.16)} !important`,
                            fontWeight: 700,
                          },
                        }),
                      }}
                    >
                      {DETAIL_COLS.map((col) => {
                        const text = fmtCell(row, col)
                        return (
                          <TableCell
                            key={col.key}
                            align={col.align ?? 'left'}
                            sx={{
                              width: col.width,
                              minWidth: col.width,
                              maxWidth: col.width,
                              fontSize: '0.68rem',
                              py: 0.3,
                              px: 0.5,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                            title={text}
                          >
                            {text}
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1 }}>
        <Button onClick={onClose} size="small">
          Close
        </Button>
      </DialogActions>
    </>
  )
}
